import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { MessageCircle, Book, Video, Mail, Phone, HelpCircle, FileText, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpCenter = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'technical',
    priority: 'medium',
    description: ''
  });

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I customize my practice branding?',
          a: 'Go to Settings → Branding tab. Upload your logo, set your brand colors, and update your practice information. Changes appear instantly on your patient-facing pages.'
        },
        {
          q: 'How do I import my existing patient data?',
          a: 'Navigate to Data Import in the admin menu. Download our CSV template, format your data, and upload. We support imports from RepeatMD, Mindbody, Boulevard, and other platforms.'
        },
        {
          q: 'How do I add treatments and pricing?',
          a: 'Go to Treatments management (coming soon) or use Data Import to bulk upload your treatment catalog with pricing and descriptions.'
        }
      ]
    },
    {
      category: 'Bookings & Appointments',
      questions: [
        {
          q: 'How do patients book appointments?',
          a: 'Patients visit your custom URL (e.g., yourpractice.mdspa360.com), browse treatments, select a time slot, and complete booking. You receive notifications for new bookings.'
        },
        {
          q: 'Can I integrate with Boulevard or other booking systems?',
          a: 'Yes! Go to Settings → Advanced and configure your booking system integration. Enter your API keys and we\'ll sync appointments automatically.'
        },
        {
          q: 'How do I manage appointment cancellations?',
          a: 'Go to Appointments, find the booking, and update the status to "cancelled". Patients receive automatic email notifications.'
        }
      ]
    },
    {
      category: 'Memberships & Payments',
      questions: [
        {
          q: 'How does credit rollover work?',
          a: 'Unused membership credits automatically roll over to the next billing cycle. This is tracked in each patient\'s account and displayed in their dashboard.'
        },
        {
          q: 'What payment processors are supported?',
          a: 'We support Stripe, Square, PayPal, and Authorize.net. Configure your preferred processor in Settings → Advanced → Payment Integration.'
        },
        {
          q: 'How do I create custom membership plans?',
          a: 'Go to Memberships management, click "Create New Plan", set pricing, credits, and benefits. Plans are immediately available for patient signup.'
        }
      ]
    },
    {
      category: 'Marketing & SMS',
      questions: [
        {
          q: 'How do I send SMS campaigns to patients?',
          a: 'Navigate to SMS Campaigns, select a template or create custom message, choose recipient group (all patients, members, inactive), and send. Track delivery rates in real-time.'
        },
        {
          q: 'Can I upload promotional videos?',
          a: 'Yes! Go to Marketing → Upload Video. Add treatment demos, testimonials, or educational content. Videos appear on your patient-facing site.'
        },
        {
          q: 'How do I track marketing campaign performance?',
          a: 'Visit Analytics dashboard to see SMS delivery rates, video views, booking conversion rates, and revenue attribution by campaign.'
        }
      ]
    },
    {
      category: 'Technical & Troubleshooting',
      questions: [
        {
          q: 'My custom domain isn\'t working',
          a: 'Contact our support team at support@mdspa360.com with your domain name. We\'ll help configure DNS settings and SSL certificates (typically takes 24-48 hours).'
        },
        {
          q: 'Patients can\'t see available time slots',
          a: 'Check: 1) Provider availability is set in Settings 2) Business hours are configured 3) No conflicting appointments. Still issues? Submit a support ticket.'
        },
        {
          q: 'How do I export my data?',
          a: 'Go to Settings → Data Export (coming soon). Select data type (patients, appointments, etc.) and download as CSV. Available for all paid plans.'
        }
      ]
    }
  ];

  const videoTutorials = [
    { title: 'Getting Started with MdSpa360', duration: '5:32', thumbnail: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400', url: '#' },
    { title: 'Customizing Your Practice Branding', duration: '3:45', thumbnail: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=400', url: '#' },
    { title: 'Setting Up Membership Plans', duration: '6:18', thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400', url: '#' },
    { title: 'Managing Appointments & Bookings', duration: '4:50', thumbnail: 'https://images.unsplash.com/photo-1512486130939-2c4f79935e4f?w=400', url: '#' }
  ];

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/support/tickets`, {
        ...ticketForm,
        practiceId: 'demo-practice-id' // Replace with actual practice ID
      });

      toast({
        title: 'Support Ticket Submitted',
        description: 'We\'ll respond within 24 hours. Check your email for updates.'
      });

      // Reset form
      setTicketForm({
        subject: '',
        category: 'technical',
        priority: 'medium',
        description: ''
      });
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: 'Please try again or email support@mdspa360.com directly.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Help Center</h1>
        <p className="text-gray-600 mt-2">Find answers, watch tutorials, and get support</p>
      </div>

      {/* Quick Contact */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <Mail className="h-8 w-8 text-blue-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Email Support</h3>
            <p className="text-sm text-gray-600 mb-3">support@mdspa360.com</p>
            <p className="text-xs text-gray-500">Response within 24 hours</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <Phone className="h-8 w-8 text-green-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Phone Support</h3>
            <p className="text-sm text-gray-600 mb-3">(555) 360-MDSPA</p>
            <p className="text-xs text-gray-500">Mon-Fri 9am-6pm EST</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <MessageCircle className="h-8 w-8 text-purple-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Live Chat</h3>
            <p className="text-sm text-gray-600 mb-3">Chat with our team</p>
            <Button size="sm" variant="outline" className="text-xs">
              Start Chat
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="faq" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="faq">
            <HelpCircle className="mr-2 h-4 w-4" />
            FAQs
          </TabsTrigger>
          <TabsTrigger value="videos">
            <Video className="mr-2 h-4 w-4" />
            Video Tutorials
          </TabsTrigger>
          <TabsTrigger value="docs">
            <Book className="mr-2 h-4 w-4" />
            Documentation
          </TabsTrigger>
          <TabsTrigger value="support">
            <FileText className="mr-2 h-4 w-4" />
            Submit Ticket
          </TabsTrigger>
        </TabsList>

        {/* FAQs Tab */}
        <TabsContent value="faq" className="space-y-6">
          {faqs.map((category, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="text-lg">{category.category}</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, qIndex) => (
                    <AccordionItem key={qIndex} value={`item-${index}-${qIndex}`}>
                      <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Video Tutorials Tab */}
        <TabsContent value="videos">
          <div className="grid md:grid-cols-2 gap-6">
            {videoTutorials.map((video, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                <div className="relative aspect-video bg-gray-900">
                  <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                      <Video className="h-8 w-8 text-gray-900" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-base">{video.title}</CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="docs">
          <Card>
            <CardHeader>
              <CardTitle>Documentation & Guides</CardTitle>
              <CardDescription>Comprehensive guides for using MdSpa360</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { title: 'Platform Overview', pages: '12 pages' },
                  { title: 'Practice Setup Guide', pages: '8 pages' },
                  { title: 'Patient Management', pages: '15 pages' },
                  { title: 'Appointment Scheduling', pages: '10 pages' },
                  { title: 'Membership Plans Setup', pages: '7 pages' },
                  { title: 'Marketing & SMS Campaigns', pages: '9 pages' },
                  { title: 'Payment Integration', pages: '11 pages' },
                  { title: 'Data Import & Export', pages: '6 pages' },
                  { title: 'API Documentation', pages: '25 pages' }
                ].map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <Book className="h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-semibold text-gray-900">{doc.title}</h4>
                        <p className="text-sm text-gray-500">{doc.pages}</p>
                      </div>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submit Ticket Tab */}
        <TabsContent value="support">
          <Card>
            <CardHeader>
              <CardTitle>Submit Support Ticket</CardTitle>
              <CardDescription>Our team will respond within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitTicket} className="space-y-6">
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    required
                    className="mt-2"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={ticketForm.category}
                      onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing & Payments</option>
                      <option value="feature">Feature Request</option>
                      <option value="integration">Integration Help</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <select
                      id="priority"
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                    placeholder="Please provide detailed information about your issue..."
                    rows={6}
                    required
                    className="mt-2"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Tip:</strong> Include screenshots, error messages, and steps to reproduce the issue for faster resolution.
                  </p>
                </div>

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? 'Submitting...' : 'Submit Support Ticket'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HelpCenter;